import { Fragment, useEffect, useState } from 'react';
import { Inventory, Product, Variant } from 'shared/types/product';
import { AccessorySelection } from 'shared/types/product/Variant';
import { Button } from 'components/ui/button';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { RouteHelpers } from 'helpers/routerHelpers';
import { cn } from 'helpers/utils/classnames';
import { gtmAddToRegistry } from 'helpers/utils/gtm-events/gtmInteraction';
import { LineItemMapper } from 'helpers/utils/gtm-events/mapper/LineItemMapper';
import myGiftRegistry from 'helpers/utils/my-gift-registry';
import { useAccount, useProduct, useProjectSettings } from 'frontastic/hooks';
import AddToMyListBtn from './AddToMyListBtn';
import DiscountBadge from './DiscountBadge';
import OptionalAccessories from './OptionalAccessories';
import ProductAttributeDisplay from './ProductAttributeDisplay';
import ProductColorTag from './ProductColorTag';
import ProductGallery from './ProductGallery';
import ProductQuantitySelector from './ProductQuantitySelector';
import ProductSizeTag from './ProductSizeTag';
import { ProductAsset } from '../../../../../../types/product/Product';
import useMediaQuery from '../../../../../helpers/hooks/useMediaQuery';
import { desktop } from '../../../../../helpers/utils/screensizes';
import SocialShare from '../../../social-share';

interface Props {
  product: Product;
  selectedVariant: Variant | undefined;
  selectedColor: string;
  labelSelectedColor: string;
  selectedSize: string;
  isQuickView?: boolean;
  setSelectedVariant?: (variant: Variant) => void;
  onAddToCart?: () => void;
}
const ProductTile = ({
  product,
  selectedColor,
  labelSelectedColor,
  selectedSize,
  selectedVariant,
  isQuickView = false,
  setSelectedVariant,
  onAddToCart,
}: Props) => {
  const originalPrice = selectedVariant?.originalPrice;
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [isDesktop] = useMediaQuery(desktop);
  const { account } = useAccount();
  const { data: config } = useProjectSettings();

  // Get all the sizes from variants
  const sizes = [
    ...new Set(
      product.variants
        .filter((variant) =>
          isQuickView
            ? variant.attributes?.actualColor === selectedColor
            : RouteHelpers.slugify(variant.attributes?.actualColor || '') === selectedColor,
        )
        .map((variant) => variant.attributes?.size),
    ),
  ];

  /**
   * Return an array of object {color: imageUrl}
   * e.g {'Cream': 'https://example.com/image.jpg'}
   */
  const groupedImagesByColor = product.variants.reduce((acc: { [color: string]: string }, variant) => {
    const color = variant.attributes?.actualColor;
    const image = variant.swatchImage || '';

    if (!acc[color]) {
      acc[color] = image;
    }
    return acc;
  }, {});

  const [selectedAccessories, setSelectedAccessories] = useState<AccessorySelection>({
    accessories: [],
    accessoriesOnly: false,
  });

  const transformProductAssets = (): ProductAsset[] => {
    const assets: ProductAsset[] = [
      ...(selectedVariant?.images || []).map((image) => ({ type: 'IMAGE' as const, src: image })),
    ];

    if (selectedVariant?.attributes?.productVideo) {
      assets.push({
        src: selectedVariant?.attributes?.productVideo,
        type: 'VIDEO' as const,
      });
    }

    return assets;
  };

  const useShadeSize = selectedVariant?.sku?.startsWith('LS') && selectedVariant?.attributes?.shadeSize;

  return (
    <>
      {isDesktop && (
        <SocialShare
          itemId={product.productId}
          productName={product.name}
          selectedVariant={selectedVariant}
          selectedImageUrl={selectedImageUrl}
        />
      )}
      <div
        className={cn('flex flex-col py-4 md:flex-row md:py-8', {
          'gap-10 md:gap-8': isQuickView,
          'gap-8 md:gap-4': !isQuickView,
        })}
      >
        <ProductGallery
          productName={product.name}
          variantSku={selectedVariant?.sku}
          isQuickView={isQuickView}
          assets={transformProductAssets()}
          onImageChange={(imageUrl: string) => setSelectedImageUrl(imageUrl)}
        />
        <div className={cn('mx-auto flex w-full flex-col md:max-w-[400px]', {})}>
          <h1 className="text-sol-300 mb-5 text-left text-[32px] font-semibold leading-10 md:text-center">
            {product.name}
          </h1>
          <p className="mb-5 text-left text-xs font-bold leading-[15px] text-gray-500 md:text-center">
            SKU: {selectedVariant?.sku}
          </p>
          <div className="flex gap-2 md:justify-center">
            <div className="flex items-center gap-2">
              {selectedVariant && selectedVariant.discountedPrice ? (
                <strong
                  style={{ color: '#ef4444' }}
                  aria-label={`Discounted price: ${CurrencyHelpers.formatForCurrency(selectedVariant.discountedPrice)}`}
                  className="block text-left text-base font-semibold leading-5 text-red-500"
                >
                  {CurrencyHelpers.formatForCurrency(selectedVariant.discountedPrice)}
                </strong>
              ) : (
                <>
                  {originalPrice && selectedVariant.price && (
                    <strong
                      style={{ color: '#ef4444' }}
                      aria-label={`Discounted price: ${CurrencyHelpers.formatForCurrency(originalPrice)}`}
                      className="block text-left text-base font-semibold leading-5 text-red-500"
                    >
                      {CurrencyHelpers.formatForCurrency(selectedVariant.price)}
                    </strong>
                  )}
                </>
              )}
              {selectedVariant && selectedVariant.price && (
                <>
                  {originalPrice ? (
                    <p className={cn('line-through')}>{CurrencyHelpers.formatForCurrency(originalPrice)}</p>
                  ) : (
                    <p className={cn({ 'line-through': selectedVariant.discountedPrice })}>
                      {CurrencyHelpers.formatForCurrency(selectedVariant.price)}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          <hr className="my-8 h-px border-t border-[#B19A6A]" />

          <ProductAttributeDisplay label="Color" value={labelSelectedColor} />

          {Object.keys(groupedImagesByColor).length > 1 && (
            <ul
              aria-labelledby="color-selection"
              role="list"
              className="my-4 flex flex-wrap justify-start gap-4 py-[6px] md:justify-center md:px-[6px]"
            >
              {Object.keys(groupedImagesByColor).map((color) => (
                <Fragment key={`product-list-card-${product.name}-${color}`}>
                  {isQuickView ? (
                    <ProductColorTag
                      color={color}
                      imageUrl={groupedImagesByColor[color]}
                      isDisabled={
                        !product.variants.find(
                          (variant) =>
                            variant.attributes?.actualColor === color && variant.attributes?.size == selectedSize,
                        )
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        const targetVariant = product.variants.find(
                          (variant) =>
                            variant.attributes?.actualColor === color && variant.attributes.size === selectedSize,
                        );
                        if (!targetVariant) return;
                        setSelectedVariant?.(targetVariant);
                      }}
                      isQuickView={isQuickView}
                      isSelectedVariant={color === selectedVariant?.attributes?.actualColor}
                    />
                  ) : (
                    <ProductColorTag
                      isDisabled={
                        !product.variants.find(
                          (variant) =>
                            variant.attributes?.actualColor === color && variant.attributes.size === selectedSize,
                        )
                      }
                      color={color}
                      url={(() => {
                        const variant = product.variants.find((v) => v.attributes?.actualColor === color);
                        const filterFinishKey = variant?.attributes?.filterFinish?.[0]?.key;

                        const urlParams =
                          sizes.length > 1
                            ? {
                                color: RouteHelpers.slugify(color),
                                size: selectedSize,
                                filterFinish: filterFinishKey,
                              }
                            : {
                                color: RouteHelpers.slugify(color),
                                filterFinish: filterFinishKey,
                              };

                        return `${product?._url}${RouteHelpers.encodeToURL(urlParams)}`;
                      })()}
                      //
                      isSelectedVariant={selectedColor === RouteHelpers.slugify(color)}
                      imageUrl={groupedImagesByColor[color]}
                    />
                  )}
                </Fragment>
              ))}
            </ul>
          )}

          {sizes.length > 1 && (
            <div className="my-4 flex flex-wrap gap-2">
              {sizes.map((size) => (
                <Fragment key={`${product.productId}-size-${size}`}>
                  {isQuickView ? (
                    <ProductSizeTag
                      isSelectedVariant={selectedSize === size}
                      size={size}
                      onClick={() => {
                        const targetVariant = product.variants.find(
                          (variant) =>
                            variant.attributes?.size === size && variant.attributes?.actualColor === selectedColor,
                        );
                        if (!targetVariant) return;
                        setSelectedVariant?.(targetVariant);
                      }}
                    />
                  ) : (
                    <ProductSizeTag
                      url={`${product?._url}${RouteHelpers.encodeToURL({ color: selectedColor, size })}`}
                      isSelectedVariant={selectedSize === size}
                      size={size}
                    />
                  )}
                </Fragment>
              ))}
            </div>
          )}
          <ProductAttributeDisplay
            label={useShadeSize ? 'Shade Size' : 'Actual Size'}
            value={useShadeSize ? selectedVariant?.attributes?.shadeSize : selectedVariant?.attributes?.actualSize}
          />
          {account && config && account.customerGroup === config.tradeAccountCustomerGroup && (
            <p className="text-sm font-semibold text-[#B19A6A]">
              Your 20% Trade Discount will be automatically applied to eligible items in your cart, if applicable.
            </p>
          )}
          <ProductAvailabilityMessage sku={selectedVariant?.sku as string} />

          {selectedVariant?.attributes?.isDiscontinued && (
            <div className="flex items-center pt-6 text-left text-sm font-semibold leading-[17px] text-red-500">
              <div className="mr-2 size-1 rounded-[50%] bg-red-500"></div>
              <p>{`Item discontinued. You may only order a maximum quantity of ${selectedVariant?.availableQuantity}`}</p>
            </div>
          )}

          {selectedVariant?.attributes?.accessoryItem && (
            <OptionalAccessories
              productVariant={selectedVariant}
              setSelectedAccessories={setSelectedAccessories}
              selectedAccessories={selectedAccessories}
            />
          )}

          <DiscountBadge
            title={product?.masterVariant?.discounts?.[0].name || selectedVariant?.attributes?.promotionalMessageTitle}
            description={
              product?.masterVariant?.discounts?.[0].description ||
              selectedVariant?.attributes?.promotionalMessageDescription
            }
          />
          <hr className="my-8 h-px border-t border-[#B19A6A]" />

          <ProductQuantityAndRegistryContainer
            product={product}
            selectedVariant={selectedVariant}
            selectedAccessories={selectedAccessories}
            onAddToCart={onAddToCart}
          />
        </div>

        {/* <ProductQuantitySelector product={product} variant={selectedVariant} /> */}
      </div>
      <MyRegistryScript />
      {!isDesktop && (
        <SocialShare
          productName={product.name}
          itemId={product.productId}
          selectedVariant={selectedVariant}
          selectedImageUrl={selectedImageUrl}
        />
      )}
    </>
  );
};

const MyRegistryScript = () => {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  if (!isLoaded) return null;
  return (
    <script>
      MyRegistrySDK.Behaviors.on(`${'gift_option_bound'}`, function(e){});
    </script>
  );
};

export interface ProductQuantityAndRegistryContainerProps {
  product: Product;
  selectedVariant?: Variant;
  selectedAccessories: AccessorySelection;
  onAddToCart?: () => void;
}

const ProductQuantityAndRegistryContainer: React.FC<ProductQuantityAndRegistryContainerProps> = ({
  product,
  selectedVariant,
  selectedAccessories,
  onAddToCart,
}) => {
  const [quantity, setQuantity] = useState<number>(1);

  return (
    <>
      <ProductQuantitySelector
        product={product}
        onChangeQuantity={(qty) => {
          setQuantity(qty);
        }}
        variant={selectedVariant}
        selectedAccessories={selectedAccessories}
        onAddToCart={onAddToCart}
      />
      <section className="flex justify-end">
        <AddToMyListBtn product={product} variant={selectedVariant} quantity={quantity} />
      </section>
      <div className="mt-[18px] flex w-full items-center justify-end">
        <Button
          aria-label="Add to Gift Registry"
          onClick={async () => {
            if (selectedVariant) {
              const color = selectedVariant?.attributes?.actualColor || '';
              const size = selectedVariant?.attributes?.size;
              const queryParams = RouteHelpers.encodeToURL({ color, size });
              const productUrl = `/products/${product.slug}${queryParams}`;
              await myGiftRegistry.addToGiftRegistry(product, selectedVariant, quantity, productUrl);
              gtmAddToRegistry({
                itemDataLayer: LineItemMapper.fromProductToItemDataLayer(product, quantity, selectedVariant),
                quantity: quantity,
              });
            }
          }}
          type="button"
          className="text-left text-sm font-semibold leading-[17px] text-primary-gray"
        >
          + Add to Gift Registry
        </Button>
      </div>
    </>
  );
};

const ProductAvailabilityMessage = ({ sku }: { sku: string }) => {
  const [inventory, setInventory] = useState<Inventory | undefined>();
  const { getAvailabilityMessage } = useProduct();
  useEffect(() => {
    async function setAvailabilityMessage() {
      const res = await getAvailabilityMessage(sku);

      if (!res.isError) {
        setInventory(res.data);
      } else {
        setInventory(undefined);
      }
    }

    setAvailabilityMessage();
  }, [sku]);

  if (inventory?.availabilityMessage) {
    return (
      <div className="flex items-center pt-6 text-left text-sm font-semibold leading-[17px] text-primary-dark">
        <div className="mr-2 size-1 rounded-[50%] bg-black"></div>
        <p>{inventory.availabilityMessage}</p>
      </div>
    );
  }
};

export default ProductTile;
